import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Button,
  Spinner,
  Breadcrumb,
  BreadcrumbItem,
} from 'reactstrap';
import classnames from 'classnames';
import { ToastContainer, toast } from 'react-toastify';
import AwardingTab from './AwardingTab';
import ActivityTab from './ActivityTab';
import useAwardingDetail from '../hooks/useAwardingDetail';
import useAwardingApi from '../hooks/useAwardingApi';
import {
  AWARDING_SENDER,
  AWARDING_STATUS,
  PROCUREMENT_TYPE_HEADER_LABEL,
  displayValue,
  getAwardingStatusConfig,
  getProcurementTypeLabel,
} from '../utils/awardingHelpers';

const TABS = [
  { id: 'awarding', label: 'Awarding', icon: 'ri-award-line' },
  { id: 'activity', label: 'Activity', icon: 'ri-history-line' },
];

const AwardingDetail = ({
  mode = 'evaluator',
  listPath = '/apps-awarding/view',
}) => {
  const { id } = useParams();
  const { confirmDealRecap } = useAwardingApi();
  const [activeTab, setActiveTab] = useState('awarding');
  const [isConfirming, setIsConfirming] = useState(false);

  const {
    awarding,
    activities,
    responses,
    isLoading,
    isLoadingActivities,
    isLoadingResponses,
    refresh,
    refreshActivities,
    refreshResponses,
  } = useAwardingDetail(
    id,
    mode === 'supplier'
      ? AWARDING_SENDER.companyMitra
      : AWARDING_SENDER.pertamina
  );

  document.title = 'Awarding Detail';

  const header = useMemo(
    () => ({
      procurementNumber: displayValue(awarding?.procurementNumber),
      categoryLabel:
        awarding?.procurementTypeHeaderLabel ||
        PROCUREMENT_TYPE_HEADER_LABEL[awarding?.procurementTypeHeader] ||
        'Product Hydro',
      procTypeLabel:
        awarding?.procurementTypeLabel ||
        getProcurementTypeLabel(awarding?.procurementType),
      titleLabel: displayValue(awarding?.titleProcurement),
      status: awarding?.statusWording || 'Open',
    }),
    [awarding]
  );

  const statusConfig = getAwardingStatusConfig(header.status);

  const handleChanged = async () => {
    await refresh();
    await refreshActivities();
  };

  const handleRespondCreated = async () => {
    await refreshResponses();
    await refreshActivities();
  };

  const isSupplier = mode === 'supplier';
  // Konfirmasi hanya untuk sisi Pertamina; role DMUT / Registered Supplier tidak punya tombol ini.
  const canConfirm =
    !isSupplier && awarding?.status === AWARDING_STATUS.inProgress;

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await confirmDealRecap(id);
      toast.success('Awarding has been confirmed');
      await handleChanged();
    } catch (error) {
      toast.error(error.message || 'Failed to confirm awarding');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <ToastContainer closeButton={false} limit={1} />

          <Breadcrumb listClassName='p-3 py-2 bg-light'>
            <BreadcrumbItem>
              <Link to='/dashboard'>Home</Link>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <Link to={listPath}>Awarding</Link>
            </BreadcrumbItem>
            <BreadcrumbItem active>Detail</BreadcrumbItem>
          </Breadcrumb>

          {isLoading ? (
            <div className='text-center py-5'>
              <Spinner color='primary' />
              <p className='text-muted small mt-2 mb-0'>
                Loading awarding detail...
              </p>
            </div>
          ) : !awarding ? (
            <Card className='shadow-none border'>
              <CardBody className='text-center py-5'>
                <i
                  className='ri-inbox-line text-muted'
                  style={{ fontSize: '3rem', opacity: 0.5 }}
                ></i>
                <p className='mt-2 mb-0 text-muted'>Awarding data not found</p>
              </CardBody>
            </Card>
          ) : (
            <>
              <Card className='shadow-none border mb-3'>
                <CardBody className='p-4 d-flex justify-content-between align-items-start gap-3'>
                  <div>
                    <p className='text-muted fs-13 mb-1'>
                      No. Proc {header.procurementNumber}
                      <span className='mx-2'>|</span>
                      {header.categoryLabel}
                    </p>
                    <h5 className='fw-bold mb-2'>
                      {header.procTypeLabel}
                      <span className='mx-2 text-muted'>•</span>
                      {header.titleLabel}
                    </h5>
                    <Badge
                      color={statusConfig.color}
                      className='px-2 py-1 fs-12 fw-medium rounded-pill'
                    >
                      {statusConfig.label}
                    </Badge>
                  </div>

                  {canConfirm && (
                    <Button
                      color='primary'
                      onClick={handleConfirm}
                      disabled={isConfirming}
                    >
                      {isConfirming ? 'Confirming...' : 'Confirm'}
                    </Button>
                  )}
                </CardBody>
              </Card>

              <Row>
                <Col lg={12}>
                  <Card className='shadow-none border'>
                    <CardBody className='p-4'>
                      <Nav tabs className='nav-tabs-custom nav-primary mb-0'>
                        {TABS.map(tab => (
                          <NavItem key={tab.id}>
                            <NavLink
                              className={classnames('cursor-pointer', {
                                active: activeTab === tab.id,
                              })}
                              onClick={() => setActiveTab(tab.id)}
                            >
                              <i
                                className={`${tab.icon} align-middle me-1`}
                              ></i>
                              <span className='fw-semibold'>{tab.label}</span>
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>

                      <TabContent activeTab={activeTab} className='pt-4'>
                        <TabPane tabId='awarding'>
                          {activeTab === 'awarding' && (
                            <AwardingTab
                              awardingId={id}
                              awarding={awarding}
                              responses={responses}
                              isLoadingResponses={isLoadingResponses}
                              onChanged={handleChanged}
                              onRespondCreated={handleRespondCreated}
                              mode={mode}
                            />
                          )}
                        </TabPane>
                        <TabPane tabId='activity'>
                          {activeTab === 'activity' && (
                            <ActivityTab
                              activities={activities}
                              isLoading={isLoadingActivities}
                              onMount={refreshActivities}
                            />
                          )}
                        </TabPane>
                      </TabContent>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AwardingDetail;
